import React, { useState, useEffect } from 'react';
import { getUsers } from '../mock/users';

const FilterBar = ({ onFilter, clients = [] }) => {
  const [filters, setFilters] = useState({
    specialty: '',
    cell: '',
    month: '',
    sortBy: 'name',
    sortOrder: 'asc',
    // Nuevos filtros
    dateRange: '',
    specificDate: '',
    weekDay: '',
    customDateFrom: '',
    customDateTo: '',
    status: '', // activo, inactivo, con juntas recientes, etc.
    conversionRate: '', // alto, medio, bajo
    salesExecutive: '',
    country: ''
  });
  
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState('');
  const [showCustomSpecialtyInput, setShowCustomSpecialtyInput] = useState(false);
  const [allExecutives, setAllExecutives] = useState([]);

  // Cargar ejecutivos desde la base de datos
  useEffect(() => {
    const loadExecutives = async () => {
      try {
        const users = await getUsers();
        const executives = users
          .filter(user => ['admin', 'capacitador', 'ejecutivo', 'standard'].includes(user.role))
          .map(user => user.fullName)
          .filter(name => name && name.trim() !== '')
          .sort((a, b) => a.localeCompare(b)); // Sort alphabetically
        console.log('Loaded executives:', executives);
        setAllExecutives(executives);
      } catch (error) {
        console.error('Error al cargar ejecutivos:', error);
        setAllExecutives([]);
      }
    };

    loadExecutives();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty' && value === 'custom') {
      setShowCustomSpecialtyInput(true);
      setFilters(prev => ({ ...prev, specialty: '' }));
    } else {
      setShowCustomSpecialtyInput(false);
      setCustomSpecialty('');
      const newFilters = {
        ...filters,
        [name]: value
      };
      setFilters(newFilters);
      onFilter(newFilters);
    }
  };

  const handleCustomSpecialtyChange = (e) => {
    setCustomSpecialty(e.target.value);
    const newFilters = {
      ...filters,
      specialty: e.target.value
    };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const clearAllFilters = () => {
    const resetFilters = {
      specialty: '',
      cell: '',
      month: '',
      sortBy: 'name',
      sortOrder: 'asc',
      dateRange: '',
      specificDate: '',
      weekDay: '',
      customDateFrom: '',
      customDateTo: '',
      status: '',
      conversionRate: '',
      salesExecutive: '',
      country: ''
    };
    setFilters(resetFilters);
    setCustomSpecialty('');
    setShowCustomSpecialtyInput(false);
    onFilter(resetFilters);
  };

  const specialties = [
    'Medicina General',
    'Cardiología',
    'Dermatología',
    'Endocrinología',
    'Gastroenterología',
    'Ginecología y Obstetricia',
    'Neurología',
    'Oncología',
    'Oftalmología',
    'Ortopedia y Traumatología',
    'Otorrinolaringología',
    'Pediatría',
    'Psiquiatría',
    'Psicología',
    'Reumatología',
    'Urología',
    'Neumología',
    'Cirugía General',
    'Cirugía Plástica y Reconstructiva',
    'Odontología',
    'Nutriología',
    'Fisioterapia'
  ];

  const cells = [
    '', 'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4',
    'G7M1', 'G7M2', 'G7M3', 'G7M4'
  ];

  const months = [
    { value: '', label: 'Todos los meses' },
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const weekDays = [
    { value: '', label: 'Todos los días' },
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' },
    { value: '6', label: 'Sábado' },
    { value: '0', label: 'Domingo' }
  ];

  // Usar ejecutivos cargados desde la base de datos
  const salesExecutives = allExecutives;

  // Extraer países únicos de los clientes
  const countries = [...new Set(
    clients.map(client => client.country).filter(Boolean)
  )];

  return (
    <div className="mb-6">
      {/* Header con toggle para filtros avanzados */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-[#0061A8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
          </svg>
          <h3 className="text-lg font-semibold text-[#0061A8]">Filtros de Búsqueda</h3>
        </div>
        <button
          onClick={clearAllFilters}
          className="flex items-center space-x-1 text-[#63666A] hover:text-[#0061A8] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Limpiar Filtros</span>
        </button>
      </div>

      {/* Filtros Básicos */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-[#63666A] mb-3 uppercase tracking-wide">Filtros Básicos</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Especialidad */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              Especialidad
            </label>
            <select
              name="specialty"
              value={showCustomSpecialtyInput ? 'custom' : filters.specialty}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              <option value="">Todas las especialidades</option>
              {specialties.map(spec => (
                <option key={spec} value={spec}>{spec}</option>
              ))}
              <option value="custom">Otra (especificar)</option>
            </select>
            {showCustomSpecialtyInput && (
              <input
                type="text"
                value={customSpecialty}
                onChange={handleCustomSpecialtyChange}
                placeholder="Especifique especialidad"
                className="mt-2 w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
            )}
          </div>

          {/* Célula */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Célula
            </label>
            <select
              name="cell"
              value={filters.cell}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {cells.map(cell => (
                <option key={cell} value={cell}>{cell === '' ? 'Todas las células' : cell}</option>
              ))}
            </select>
          </div>

          {/* Mes */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Mes
            </label>
            <select
              name="month"
              value={filters.month}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Filtros por Fecha */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-[#63666A] mb-3 uppercase tracking-wide">Filtros por Fecha</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Fecha Específica */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Fecha Específica
            </label>
            <input
              type="date"
              name="specificDate"
              value={filters.specificDate}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>

          {/* Día de la Semana */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Día de la Semana
            </label>
            <select
              name="weekDay"
              value={filters.weekDay}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {weekDays.map(day => (
                <option key={day.value} value={day.value}>{day.label}</option>
              ))}
            </select>
          </div>

          {/* Semana */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m0 0h2m-2 0v4a2 2 0 002 2h2a2 2 0 002-2v-4m0 0h2a2 2 0 002-2V7a2 2 0 00-2-2h-2m-4 0V3a2 2 0 00-2-2H9a2 2 0 00-2 2v2m4 0h2" />
              </svg>
              Semana
            </label>
            <select
              name="dateRange"
              value={filters.dateRange}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              <option value="">Seleccionar semana</option>
              <option value="thisWeek">Esta semana</option>
              <option value="lastWeek">Semana pasada</option>
              <option value="nextWeek">Próxima semana</option>
              <option value="thisMonth">Este mes</option>
              <option value="lastMonth">Mes pasado</option>
              <option value="nextMonth">Próximo mes</option>
            </select>
          </div>

          {/* Rango Personalizado */}
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Rango Personalizado
            </label>
            <div className="flex space-x-1">
              <input
                type="date"
                name="customDateFrom"
                value={filters.customDateFrom}
                onChange={handleChange}
                placeholder="Desde"
                className="w-1/2 border border-[#D1D3D4] rounded-md shadow-sm py-2 px-2 text-xs focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
              <input
                type="date"
                name="customDateTo"
                value={filters.customDateTo}
                onChange={handleChange}
                placeholder="Hasta"
                className="w-1/2 border border-[#D1D3D4] rounded-md shadow-sm py-2 px-2 text-xs focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Toggle para Filtros Avanzados */}
      <div className="mb-4">
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="flex items-center space-x-2 text-[#0061A8] hover:text-[#A8D08D] transition-colors"
        >
          <svg 
            className={`w-4 h-4 transform transition-transform ${showAdvancedFilters ? 'rotate-90' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
          <span>Filtros Avanzados</span>
        </button>
      </div>

      {/* Filtros Avanzados */}
      {showAdvancedFilters && (
        <div className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Estado del Cliente */}
            <div>
              <label className="block text-sm font-medium text-[#63666A] mb-1">
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Estado del Cliente
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleChange}
                className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="">Todos los estados</option>
                <option value="active">Activos (con juntas recientes)</option>
                <option value="inactive">Inactivos (sin juntas recientes)</option>
                <option value="withMeetings">Con juntas programadas</option>
                <option value="withoutMeetings">Sin juntas programadas</option>
              </select>
            </div>

            {/* Tasa de Conversión */}
            <div>
              <label className="block text-sm font-medium text-[#63666A] mb-1">
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Tasa de Conversión
              </label>
              <select
                name="conversionRate"
                value={filters.conversionRate}
                onChange={handleChange}
                className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="">Todas las tasas</option>
                <option value="high">Alta (&gt;70%)</option>
                <option value="medium">Media (30-70%)</option>
                <option value="low">Baja (&lt;30%)</option>
                <option value="zero">Sin conversión (0%)</option>
              </select>
            </div>

            {/* Ejecutivo de Ventas */}
            <div>
              <label className="block text-sm font-medium text-[#63666A] mb-1">
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Ejecutivo de Ventas
              </label>
              <select
                name="salesExecutive"
                value={filters.salesExecutive}
                onChange={handleChange}
                className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="">Todos los ejecutivos</option>
                {salesExecutives.map(exec => (
                  <option key={exec} value={exec}>{exec}</option>
                ))}
              </select>
            </div>

            {/* País */}
            <div>
              <label className="block text-sm font-medium text-[#63666A] mb-1">
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                País
              </label>
              <select
                name="country"
                value={filters.country}
                onChange={handleChange}
                className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="">Todos los países</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Ordenamiento */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-[#63666A] mb-3 uppercase tracking-wide">Ordenamiento</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              Ordenar por
            </label>
            <select
              name="sortBy"
              value={filters.sortBy}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              <option value="name">Nombre</option>
              <option value="specialty">Especialidad</option>
              <option value="conversion">Tasa de conversión</option>
              <option value="lastMeeting">Última junta</option>
              <option value="totalMeetings">Total de juntas</option>
              <option value="totalContacts">Total de contactos</option>
              <option value="totalPatients">Total de pacientes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              Orden
            </label>
            <select
              name="sortOrder"
              value={filters.sortOrder}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              <option value="asc">Ascendente (A-Z, menor a mayor)</option>
              <option value="desc">Descendente (Z-A, mayor a menor)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
